import { useCallback, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import CompanyList from 'components/MeasurementDates/components/company-list/CompanyList';
import FundList from 'components/MeasurementDates/components/fund-list/FundList';
import { useMDContext } from 'context/MDContext';
import { gridShortDate, nextQuarterEndDate } from 'utilities/dateFormats';
import { CompaniesSelectorProps } from './CompaniesSelector.props';
import { FullWidthDatePicker, MarginTopGrid } from './CompaniesSelector.styles';
import { SuggestedDate } from './suggested-date';
import getSuggestedQuarterFiscalEndDate, {
  isValidCompanyFiscalYear,
} from '../utilities/getSuggestedQuarterFiscalEndDate';

const CompaniesSelector = ({ activeCompany }: CompaniesSelectorProps) => {
  const {
    measurementDate,
    setMeasurementDate,
    errors,
    items,
    isLoading,
    setSelectedFunds,
    selectedFunds,
    selectedCompanies,
    setSelectedCompanies,
  } = useMDContext();
  const [date, setDate] = useState<Date | null>(measurementDate ? new Date(measurementDate) : null);

  const nextQuarterEnd = nextQuarterEndDate();
  let suggestedQuarterFiscalEnd = '';
  if (activeCompany && isValidCompanyFiscalYear(activeCompany.fiscal_year_end)) {
    suggestedQuarterFiscalEnd = getSuggestedQuarterFiscalEndDate(activeCompany.fiscal_year_end);
  }
  const handleChange = useCallback((newDate: Date | null) => {
    setDate(newDate);
  }, []);

  const handleAccept = useCallback((newDate: Date | null) => {
    if (newDate) {
      setMeasurementDate(gridShortDate(newDate));
    }
  }, [setMeasurementDate]);

  const handleFocus = useCallback(() => {
    if (!date) {
      setDate(new Date());
    }
  }, [date]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value);
    if (measurementDate !== event.target.value) {
      handleAccept(newDate);
    }
  }, [handleAccept, measurementDate]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const newDate = new Date(e.currentTarget.value);
      handleAccept(newDate);
    }
  }, [handleAccept]);

  const handleClickSuggested = useCallback((suggestedDate: string) => {
    const newDate = new Date(suggestedDate);
    setDate(newDate);
    handleAccept(newDate);
  }, [handleAccept]);

  return (
    <>
      <Grid size={{ xs: 6 }}>
        <FullWidthDatePicker
          value={date}
          format="MM/dd/yyyy"
          onChange={handleChange}
          onAccept={handleAccept}
          openTo={'day'}
          views={['year', 'month', 'day']}
          label="Measurement Date"
          slotProps={{
            textField: {
              variant: 'standard',
              margin: 'normal',
              id: 'new-measurement-date',
              name: 'new-measurement-date',
              onFocus: handleFocus,
              onBlur: handleBlur,
              onKeyDown,
              helperText: errors.date,
              error: !!errors.date,
              placeholder: 'SELECT DATE',
            },
          }}
        />
        {!isEmpty(suggestedQuarterFiscalEnd) ? (
          <SuggestedDate suggestedDate={suggestedQuarterFiscalEnd} handleClickSuggested={handleClickSuggested} />
        ) : (
          <SuggestedDate suggestedDate={nextQuarterEnd} handleClickSuggested={handleClickSuggested} />
        )}
      </Grid>
      <MarginTopGrid container spacing={4}>
        <Grid size={{ xs: 6, md: 6 }}>
          <FundList
            items={items}
            isLoading={isLoading}
            setSelectedCompanies={setSelectedCompanies}
            selectedCompanies={selectedCompanies}
            setSelectedFunds={setSelectedFunds}
            selectedFunds={selectedFunds}
            isDeletingMD={false}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <CompanyList
            items={items}
            isLoading={isLoading}
            setSelectedCompanies={setSelectedCompanies}
            selectedCompanies={selectedCompanies}
            isDeletingMD={false}
          />
        </Grid>
      </MarginTopGrid>
    </>
  );
};

export default CompaniesSelector;
